import { modelOptions, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import {
    NumericVersionSegment,
    StoredTextVersionSegmentString,
    TextVersionSegmentString,
} from "../../validators/index.js";
import { getZodSchemaFromClass, mongooseValidatorFromZod, validate } from "../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type {
    CompleteDeviceVersionFieldsWithDefaults,
    CompleteDeviceVersionSchemaTypes,
} from "../types/completeDeviceVersion.d.js";
import { type DtoValidatorTypes } from "../validatableClass.js";
import type { DEVICE_MANUFACTURER } from "./deviceManufacturer.js";
import type { HardwareVariant, ProductLine } from "./deviceVersion.js";

const SCHEMA_KEY = "CompleteDeviceVersion" as const;

/** Checks the raw value: `uppercase` alone would turn e.g. "maß" into a well-formed "MASS". */
const rawValueCheck = (zodValidator: z.ZodType) => (value: unknown) => {
    if (typeof value === "string" && !zodValidator.validate(value)) {
        throw new Error(`"${value}" is malformed`);
    }
    return value;
};

/** `set` must follow `uppercase` so it sees the raw value. */
const uppercaseCheckedBy = (rawZodValidator: z.ZodType) => ({ uppercase: true, set: rawValueCheck(rawZodValidator) });

const segmentValidators = (zodValidator: z.ZodType) => [
    mongooseValidatorFromZod(zodValidator),
    { validator: (value: unknown) => value !== null, message: "{PATH} must be absent when unknown, not null" },
];

/**
 * A DeviceVersion qualified by its manufacturer, e.g. `verdi.4.block.5.a.12` in string form. Text is stored uppercase;
 * an unknown segment is absent (`?` in string form), e.g. `verdi.4.block.?.a.12`.
 */
@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<CompleteDeviceVersion>>(...addSchemaKeyHook(SCHEMA_KEY))
export class CompleteDeviceVersion extends SchemaBase<CompleteDeviceVersion, CompleteDeviceVersionFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    /** Known values autocomplete; other letter-only manufacturers are allowed. */
    @validate({ zodValidator: StoredTextVersionSegmentString, responseOnly: true })
    @prop({
        type: () => String,
        required: true,
        ...uppercaseCheckedBy(TextVersionSegmentString),
        validate: segmentValidators(StoredTextVersionSegmentString),
    })
    public manufacturer!: DEVICE_MANUFACTURER | (string & Record<never, never>);

    @validate({ zodValidator: NumericVersionSegment.optional(), responseOnly: true })
    @prop({ type: () => Number, required: false, validate: segmentValidators(NumericVersionSegment) })
    public generation?: number;

    /** Known values autocomplete; other letter-only product lines are allowed. */
    @validate({ zodValidator: StoredTextVersionSegmentString.optional(), responseOnly: true })
    @prop({
        type: () => String,
        required: false,
        ...uppercaseCheckedBy(TextVersionSegmentString),
        validate: segmentValidators(StoredTextVersionSegmentString),
    })
    public productLine?: Uppercase<ProductLine> | (string & Record<never, never>);

    @validate({ zodValidator: NumericVersionSegment.optional(), responseOnly: true })
    @prop({ type: () => Number, required: false, validate: segmentValidators(NumericVersionSegment) })
    public hardwareVersion?: number;

    /** Known values autocomplete; other letter-only variants are allowed. */
    @validate({ zodValidator: StoredTextVersionSegmentString.optional(), responseOnly: true })
    @prop({
        type: () => String,
        required: false,
        ...uppercaseCheckedBy(TextVersionSegmentString),
        validate: segmentValidators(StoredTextVersionSegmentString),
    })
    public hardwareVariant?: Uppercase<HardwareVariant> | (string & Record<never, never>);

    @validate({ zodValidator: NumericVersionSegment.optional(), responseOnly: true })
    @prop({ type: () => Number, required: false, validate: segmentValidators(NumericVersionSegment) })
    public firmwareVersion?: number;

    static getters = {} as const satisfies Record<string, (self: CompleteDeviceVersion) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<CompleteDeviceVersionSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<CompleteDeviceVersion, CompleteDeviceVersionFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

/** The object form without the schemaKey, e.g. a producer's input or a parsed version string. */
export type CompleteDeviceVersionObject = Omit<CompleteDeviceVersion, "schemaKey">;

CompleteDeviceVersion.dtoValidators = getZodSchemaFromClass(
    CompleteDeviceVersion,
) as unknown as DtoValidatorTypes<CompleteDeviceVersionSchemaTypes>;
